import React from 'react'
import { CustomCellRendererProps } from 'ag-grid-react'
import { InflightNegotiations, NegotiationActions, NegotiationDetail, NegotiationType, ownOrderToSide } from 'api'
import { Locale, Translation } from 'locale'
import { useDispatch, useSelector } from 'react-redux'
import { initiatedNegotiationsCountByRfqIdGetterSelector } from 'selectors/negotiations'
import { NotificationType } from 'store/notification'
import { TypedValueGetterParams } from 'view/components/datagrid'
import { iconSet } from 'view/components/icons/iconSet'
import { HoverAccentType, IconActionItem, PositiveNegativeIconsButtonDropdown, PositiveNegativeType } from 'view/components/menus'
import { useModal } from 'view/shared/modal'
import { canAccept, canBackToStaging, canCancel, canRefresh, canReject, canSend, canWithdraw } from '../shared/utils'
import { initiatedInflightDetailsSelector } from './selectors'
import {
  canAcceptGridView,
  canBackToStagingGridView,
  canCancelGridView,
  canRefreshGridView,
  canRejectGridView,
  canSendGridView,
  canWithdrawGridView,
  decodeColIdValue,
  getCounterpartyNegotiations,
  inflightNegotiationsRowDataGetter,
  isNotExpiredOrRejected
} from './utils'

export const InitiatedGridViewActionsButton = (props: CustomCellRendererProps<InflightNegotiations> & { 'data-test-id': string }) => {
  const { 'data-test-id': dataTestId } = props
  const negotiations = inflightNegotiationsRowDataGetter(props as TypedValueGetterParams<InflightNegotiations>)
  const actions = useGetActions(negotiations, { appliesToAll: true })

  if (!negotiations?.length) return null

  return (
    <PositiveNegativeIconsButtonDropdown
      data-test-id={dataTestId}
      showDisabledActions
      showMainActions={negotiations.every(isNotExpiredOrRejected)}
      actions={actions}
    />
  )
}

export const InitiatedGridViewHeaderActionsButton = (props: CustomCellRendererProps<InflightNegotiations> & { 'data-test-id': string }) => {
  const { 'data-test-id': dataTestId } = props
  const counterpartyId = decodeColIdValue(props?.column?.getColDef())
  const inflights = useSelector(initiatedInflightDetailsSelector)
  const allCounterpartyNegotiations = inflights.flatMap(i => getCounterpartyNegotiations(i, counterpartyId))
  const actions = useGetActions(allCounterpartyNegotiations, { appliesToAll: false })

  if (!allCounterpartyNegotiations?.length) return null

  return (
    <div data-test-id={dataTestId}>
      <PositiveNegativeIconsButtonDropdown data-test-id={dataTestId} showDisabledActions actions={actions} />
    </div>
  )
}

const useGetActions = (details: NegotiationDetail[] | null, options: { appliesToAll: boolean }): IconActionItem[] => {
  const dispatch = useDispatch()
  const { showDialog } = useModal()
  const getNegotiationCountWithRfqId = useSelector(initiatedNegotiationsCountByRfqIdGetterSelector)

  if (!details?.length) return []

  const acceptDetails = options.appliesToAll ? details : details.filter(canAcceptGridView)
  const sendDetails = options.appliesToAll ? details : details.filter(canSendGridView)
  const refreshDetails = options.appliesToAll ? details : details.filter(canRefreshGridView)
  const withdrawDetails = options.appliesToAll ? details : details.filter(canWithdrawGridView)
  const cancelDetails = options.appliesToAll ? details : details.filter(canCancelGridView)
  const backToStagingDetails = options.appliesToAll ? details : details.filter(canBackToStagingGridView)
  const rejectDetails = options.appliesToAll ? details : details.filter(canRejectGridView)

  return [
    {
      id: Translation.TC_ACTION_ACCEPT,
      label: getLabel(Translation.TC_ACTION_ACCEPT, acceptDetails.length),
      icon: iconSet.checkIcon,
      type: PositiveNegativeType.Good,
      accentColor: HoverAccentType.Primary,
      onClick: () => {
        dispatch(
          NegotiationActions.acceptBulk(
            acceptDetails.map(d => ({
              negotiationId: d.negotiation.negotiationId,
              negotiationSide: ownOrderToSide(d.ownOrder),
              quoteResponseId: d.negotiation.negotiationId
            }))
          )
        )
      },
      enabled: !!acceptDetails.length && acceptDetails.every(canAccept)
    },
    {
      id: Translation.TC_ACTION_SEND,
      label: getLabel(Translation.TC_ACTION_SEND, sendDetails.length),
      icon: iconSet.forwardArrowIcon,
      type: PositiveNegativeType.Good,
      accentColor: HoverAccentType.Secondary,
      onClick: () => {
        sendDetails.forEach(d => {
          dispatch(NegotiationActions.counter(d.negotiation.negotiationId, d.ownOrder, false))
        })
      },
      enabled: !!sendDetails.length && sendDetails.every(canSend)
    },
    {
      id: Translation.TC_ACTION_REFRESH,
      label: getLabel(Translation.TC_ACTION_REFRESH, refreshDetails.length),
      icon: iconSet.refreshIcon,
      type: PositiveNegativeType.Good,
      accentColor: HoverAccentType.Secondary,
      onClick: () => {
        refreshDetails.forEach(d => {
          dispatch(NegotiationActions.counter(d.negotiation.negotiationId, d.ownOrder, true))
        })
      },
      enabled: !!refreshDetails.length && refreshDetails.every(canRefresh)
    },
    {
      id: Translation.TC_ACTION_WITHDRAW,
      icon: iconSet.backArrowIcon,
      type: PositiveNegativeType.Good,
      accentColor: HoverAccentType.Bad,
      label: getLabel(Translation.TC_ACTION_WITHDRAW, withdrawDetails.length),
      onClick: () => {
        withdrawDetails.forEach(d => {
          dispatch(NegotiationActions.withdraw(d.negotiation.negotiationId, d.ownOrder))
        })
      },
      enabled: !!withdrawDetails.length && withdrawDetails.every(canWithdraw)
    },
    {
      id: Translation.TC_ACTION_CANCEL,
      icon: iconSet.cancelIcon,
      type: PositiveNegativeType.Bad,
      accentColor: HoverAccentType.Bad,
      label: getLabel(Translation.TC_ACTION_CANCEL, cancelDetails.length),
      onClick: async () => {
        const canCancelAndStage = cancelDetails.every(negotiationDetail => {
          const { parentOrderId, negotiationType } = negotiationDetail.negotiation

          if (negotiationType !== NegotiationType.INDIVIDUAL) return

          const totalNumberOfNegotiationsWithParentOrderId = getNegotiationCountWithRfqId(parentOrderId)

          return (
            cancelDetails.filter(({ negotiation }) => negotiation.parentOrderId === parentOrderId).length ===
            totalNumberOfNegotiationsWithParentOrderId
          )
        })

        const dialogArgs = {
          type: NotificationType.Confirm,
          title: Translation.TC_CANCEL_RFQ,
          message: Translation.SC_CANCEL_RFQ_CONFIRMATION,
          buttons: [
            Translation.TC_BUTTON_YES,
            ...(canCancelAndStage ? [Translation.SC_BUTTON_YES_AND_STAGE] : []),
            Translation.TC_BUTTON_NO
          ],
          cancelButton: Translation.TC_BUTTON_NO,
          defaultButton: Translation.TC_BUTTON_YES
        }
        const dialogResult = await showDialog(dialogArgs)

        if (dialogResult === Translation.TC_BUTTON_YES) {
          dispatch(
            NegotiationActions.cancelBulk(
              cancelDetails.map(negotiationDetail => negotiationDetail.negotiation.negotiationId),
              false
            )
          )
        }

        if (dialogResult === Translation.SC_BUTTON_YES_AND_STAGE) {
          dispatch(
            NegotiationActions.cancelBulk(
              cancelDetails.map(negotiationDetail => negotiationDetail.negotiation.negotiationId),
              true
            )
          )
        }
      },
      enabled: !!cancelDetails.length && cancelDetails.every(canCancel)
    },
    {
      id: Translation.TC_ACTION_BACK_TO_STAGING,
      icon: iconSet.cancelIcon,
      type: PositiveNegativeType.Bad,
      accentColor: HoverAccentType.Bad,
      label: getLabel(Translation.TC_ACTION_BACK_TO_STAGING, backToStagingDetails.length),
      onClick: () => {
        backToStagingDetails.forEach(d => {
          dispatch(NegotiationActions.backToStaging(d.negotiation.negotiationId, d.ownOrder))
        })
      },
      enabled: !!backToStagingDetails.length && backToStagingDetails.every(canBackToStaging)
    },
    {
      id: Translation.TC_ACTION_REJECT,
      label: getLabel(Translation.TC_ACTION_REJECT, rejectDetails.length),
      onClick: () => {
        rejectDetails.forEach(d => {
          dispatch(NegotiationActions.reject(d.negotiation.negotiationId, d.ownOrder))
        })
      },
      enabled: !!rejectDetails.length && rejectDetails.every(canReject)
    }
  ]
}

const getLabel = (translation: Translation, count: number) => {
  return `${Locale.translate(translation)} (${count})`
}
